import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { PageProps } from 'types';

import { AppProvider } from 'contexts/appContext';
import { HistoryProvider } from 'hooks';
import { jobService } from 'services/jobService';
import { jobListingHash } from 'store/jobs/actions';
import HeaderLogoOnly from 'widgets/header/HeaderLogoOnly';
import HeaderWithProgressBar from 'widgets/header/HeaderWithProgressBar';
import HeaderWithSteps from 'widgets/header/headerWithSteps';

import '../../utils/nativeUtils';

import { useStore } from '../store';
import {
  checkLocalStorage,
  getCookie,
  isUserOutsideUSV2,
  loadServiceWorker,
  sendAnalytics,
} from '../utils';
import { GOOGLE_AD_SENSE_PUBLIC_KEY, HIDE_GOOGLE_ADS_ROUTES } from '../utils/constant';
import Header from '../widgets/header';

import '../css/application.css';
import '../less/less.css';
import '../components/index.css';
import '../layouts/index.css';
import '../widgets/index.css';
import 'styles/app.global.scss';

const Footer = dynamic(() => import('../widgets/Footer'));

const paidFixedPosCookie = '_zippia-paidFixedPos';
const joblistVsCarouselCookie = '_zippia-joblistVsCarousel';

const EZOIC_PLACEHOLDER = 'ezoic-pub-ad-placeholder';

type EzoicAd = {
  cmd?: (() => void)[];
  setEzoicAnchorAd?(data: boolean): void;
  define?(...data: number[]): void;
  enable?(): void;
  display?(): void;
}

declare global {
  interface Window {
    userAgent: string;
    pageCategory: string;
    userId: string;
    isLoggedIn: boolean;
    userGroup: string;
    GA_INITIALIZED: boolean;
    btoa: (value: string) => string;
    ezstandalone: EzoicAd;
    lastEzoicPlaceholderID: number;
  }
}

if (!global.window) {
  global.window = {
    navigator: {
      userAgent: 'node',
    },
    userAgent: 'node',
    location: {
      protocol: 'http:',
      hostname: 'localhost',
      port: '8000',
    },
  } as Window & typeof globalThis;
}

const isServer = typeof window === 'undefined';

if (isServer) {
  // eslint-disable-next-line no-console
  console.log = () => null;
}

if (typeof document === 'undefined') {
  global.document = {
    querySelector: () => null,
  } as unknown as Document;
}

window.btoa = (str) => Buffer.from(str).toString('base64');

export function reportWebVitals(metric: NextWebVitalsMetric) {
  switch (metric.name) {
    case 'FCP':
      sendAnalytics(metric);
      break;
    case 'LCP':
      sendAnalytics(metric);
      break;
    case 'CLS':
      sendAnalytics(metric);
      break;
    case 'FID':
      sendAnalytics(metric);
      break;
    case 'TTFB':
      sendAnalytics(metric);
      break;
    case 'Next.js-hydration':
      sendAnalytics(metric);
      break;
    case 'Next.js-route-change-to-render':
      sendAnalytics(metric);
      break;
    case 'Next.js-render':
      sendAnalytics(metric);
      break;
    default:
      break;
  }
}

const getEzoicPlaceholderID = (id: number) => `${EZOIC_PLACEHOLDER}-${id}`;

const initEzoicAd = (showAd: boolean) => (e: HTMLElement) => {
  if (!showAd) return;

  const ids: number[] = [];
  const placeholders = e?.querySelectorAll(`[id^='${EZOIC_PLACEHOLDER}']`);
  placeholders?.forEach((item) => item.parentNode.removeChild(item));

  const headings = e?.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings?.forEach((item, idx) => {
    const id = 100 + idx + 1;
    ids.push(id);
    const placeholderID = getEzoicPlaceholderID(id);
    const placeholder = document.createElement('div');
    placeholder.id = placeholderID;
    item.parentNode.insertBefore(placeholder, item)
  })

  const lastID = ids.at(-1);

  if (!window.lastEzoicPlaceholderID && ids.length) {
    window.ezstandalone = window?.ezstandalone || {};
    window.ezstandalone.cmd = window.ezstandalone.cmd || [];
    window.ezstandalone.cmd.push(function() {
      window.ezstandalone.setEzoicAnchorAd(false);
      window.ezstandalone.define(...ids);
      window.ezstandalone.enable();
      window.ezstandalone.display();
    });
    window.lastEzoicPlaceholderID = lastID;
  }
}

export default function App({
  Component,
  pageProps,
}: AppProps<
  PageProps<{
    resumeData: Record<string, unknown>;
    stripeSubscriptionData: Record<string, unknown>;
  }>
>) {
  const router = useRouter();
  const store = useStore(pageProps, router);
  const [state, setState] = useState({
    showNewJobSearchUI: true,
    enablePaidFixedPos: false,
    showMiniLeftPanelUI: false,
  });

  checkLocalStorage();

  useEffect(() => {
    async function fetchListingHashes() {
      try {
        const listingHashes = await jobService.getUserlistinghashes();
        store.dispatch(jobListingHash(listingHashes));
      } catch (err) {
        console.error('Error occurred while fetching listing hashes');
      }
    }

    function setCookiesUsingUserGroups() {
      const userGroupPaidFixedPos = getCookie(paidFixedPosCookie);
      const userGroupMiniLeftPanelUI = getCookie(joblistVsCarouselCookie);

      let enablePaidFixedPos = false;
      let showMiniLeftPanelUI = false;

      if (userGroupPaidFixedPos === 'groupB') {
        enablePaidFixedPos = true;
      }

      if (userGroupMiniLeftPanelUI === 'groupB') {
        showMiniLeftPanelUI = true;
      }

      setState((state) => ({
        ...state,
        enablePaidFixedPos,
        showMiniLeftPanelUI,
      }));
    }

    fetchListingHashes();

    setCookiesUsingUserGroups();

    window.addEventListener('load', loadServiceWorker);
    return () => {
      window.removeEventListener('load', loadServiceWorker);
    };
  }, [store]);

  const hideGoogleAd = HIDE_GOOGLE_ADS_ROUTES.some((route) => router?.asPath?.startsWith(route));

  const hideFooter = pageProps?.data?.notShowFooter || pageProps?.data?.notShowHeaderAndFooter;
  const isOutsideUS = isUserOutsideUSV2(pageProps?.data?.criteria);
  const showEzoicAd =
    pageProps?.locals?.enableEzoicAd &&
    pageProps?.data?.criteria?.geoCountryCode &&
    !pageProps?.data?.hideEzoicAd &&
    isOutsideUS;

  const getHeaderComponent = () => {
    if (pageProps?.data?.showHeaderOnlyLogo) {
      return <HeaderLogoOnly />;
    }

    if (pageProps?.data?.showHeaderWithSteps) {
      return (
        <HeaderWithSteps
          locals={pageProps.locals}
          criteria={pageProps.data.criteria}
          metadata={pageProps.data.metadata}
          steps={pageProps.data.headerSteps?.steps || []}
          currentStep={pageProps.data.headerSteps?.currentStep || ''}
        />
      );
    }

    if (pageProps?.data?.showHeaderWithProgressBar) {
      return <HeaderWithProgressBar criteria={pageProps.locals.criteria} />;
    }

    if (pageProps?.data?.notShowHeaderAndFooter) {
      return null;
    }

    return <Header {...pageProps.data} />;
  };

  return (
    <HistoryProvider>
      <Provider store={store}>
        <Head>
          <meta
            name="viewport"
            content="height=device-height, width=device-width, initial-scale=1.0, viewport-fit=cover"
          />
          {showEzoicAd && (
            <>
              <script async src="//www.ezojs.com/ezoic/sa.min.js" />
            </>
          )}
        </Head>

        {!hideGoogleAd && (
          <Script
            async
            id="Adsense-id"
            crossOrigin="anonymous"
            strategy="afterInteractive"
            onError={(e) => {
              console.error('Script failed to load', e);
            }}
            src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${GOOGLE_AD_SENSE_PUBLIC_KEY}`}
          />
        )}

        {getHeaderComponent()}

        <AppProvider value={state}>
          <main ref={initEzoicAd(showEzoicAd)}>
            <Component {...pageProps} />
          </main>
        </AppProvider>

        {pageProps.data && !hideFooter && (
          <Footer showSalesContactInfo={pageProps.data?.showSalesContactInfo} />
        )}
      </Provider>
    </HistoryProvider>
  );
}
